<script setup lang="ts">
import {computed, PropType, ref, watch, inject} from "vue";
import {
  AddonConfig,
  FormFieldLayout,
  FormFieldMenu,
  FormFieldSize
} from "/@src/components/custom/form/BaseFormField.vue";
import {useFormField} from "/@src/composable/form/useFormField";
import {StateChecker, Transform} from "/@src/components/custom/form/BasePanelForm.vue";
import Multiselect from "@vueform/multiselect";
import {injectRegisterFormResetHooks} from "/@src/constants/keys";

export type SelectAddonType = 'input'

export type SelectMode = 'single'|'multiple'|'tags'

export interface SelectOption<T,K=void> {
  label: string
  value: T
  extra?: K
}

export interface GroupSelectOption<T> {
  label: string
  options: Array<SelectOption<T>>
}

const props = defineProps({
  loading: Boolean,
  label: String,
  placeholder: String,
  allowAbsent: Boolean,
  required: Boolean,
  valueProp:{
    type: String,
    default: 'value',
  },
  labelProp:{
    type: String,
    default: 'label',
  },
  canDeselect: {
    type: Boolean,
    default: false,
  },
  onCreate:{
    type: Function
  },
  mode:{
    type: String as PropType<SelectMode>,
    default: 'single',
  },
  modelValue: {
    type: [Number, String, Array,Object],
  },
  disabled: {
    type: Boolean,
  },
  index: Number,
  width:{
    type: String as PropType<FormFieldSize>,
    default: 'full'
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  allowCreate: {
    type: Boolean,
    default: false,
  },
  field: {
    type: String,
  },
  options: {
    type: [Array ,Function],
  },
  layout: {
    type: String as PropType<FormFieldLayout>
  },
  deepWatch: {
    type: Boolean,
    default: false,
  },
  addonConfig:{
    type: Object as PropType<AddonConfig>,
  },
  transform: {
      type: Object as PropType<Transform<any,any>>
  },
  object:Boolean,
  immediate:Boolean,
  only:Boolean,
  trackBy:{
    type: String,
  },
  noResultsText:{
    type: String,
    default: '无匹配结果'
  },
  noOptionsText:{
    type: String,
    default: '无选项'
  },
  checker:{
    type: Object as PropType<StateChecker<any>>,
  },
  filterResults:{
    type: Boolean,
    default: true,
  },
  closeOnSelect:{
    type: Boolean,
    default: true,
  },
  /**
   * 是否分组
   */
  groups:Boolean,
  groupOptions:{
    type: Array
  },
  menus:{
    type: Array as PropType<FormFieldMenu[]>,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'onChange', value: any): void
  (e: 'onCreate', value: any): void
}>()

const localModel = ref(props.modelValue)
const refSelect = ref<InstanceType<typeof Multiselect>>()
const registerReset = inject(injectRegisterFormResetHooks)
const {visible,disabled:componentDisabled} = useFormField(localModel,props.field,{
  transform:props.transform,
  deepWatch:props.deepWatch,
  immediate:props.immediate,
  only:props.only,
  checker:props.checker,
  index: props.index,
  dataWatcher: (newVal)=>{
    emit('update:modelValue',newVal)
  }
})

watch(()=>props.modelValue,(newVal)=>{
  localModel.value = newVal
},{immediate:props.immediate})

const isOptionsAsync = computed(()=>{
  return typeof props.options === 'function'
})

const isDisabled = computed(()=>{
  return props.disabled || componentDisabled.value
})

const refreshOptions = ()=>{
  refSelect.value?.refreshOptions()
}

const select = (value:any) => {
  refSelect.value?.select(value)
}

const padding = computed(()=>{
  if (props.groups){
    return '0'
  }
  return '1rem 0.75rem'
})

const reset = ()=>{
  localModel.value = []
}

registerReset?.(reset)

defineExpose({
  refreshOptions,
  select
})

</script>

<template>
  <base-form-field
    v-show="visible"
    :layout="layout"
    :width="width"
    :disabled="isDisabled"
    :required="required"
    :menus="menus"
    :label="label">
   <div class="w-full flex items-center gap-2">
     <Multiselect
         ref="refSelect"
         v-model="localModel"
         class="formSelect"
         :label="labelProp"
         :object="object"
         :mode="mode"
         :close-on-select="closeOnSelect"
         :groups="groups"
         :allow-absent="allowAbsent"
         :value-prop="valueProp"
         :track-by="trackBy"
         :filter-results="filterResults"
         :searchable="searchable"
         :disabled="isDisabled"
         :create-option="allowCreate"
         :on-create="onCreate"
         :can-deselect="canDeselect"
         :delay="isOptionsAsync?400:-1"
         :loading="loading"
         :can-clear="false"
         :placeholder="placeholder"
         :options="options||groupOptions"
         :no-results-text="noResultsText"
         :no-options-text="noOptionsText"
         @change="(v)=>emit('onChange',v)"
     />
     <V-Button v-if="addonConfig?.button" :disabled="isDisabled"  color="primary" raised @click="addonConfig.button.onClick?.()">
       {{addonConfig?.button.text}}
     </V-Button>
   </div>
  </base-form-field>
</template>

<style lang="scss" scoped>

.multiselect{
  --ms-max-height: 21rem;
  min-width: 110px;
  ::v-deep(.multiselect-options){
    padding: v-bind(padding);
  }
  ::v-deep(.multiselect-dropdown){
    z-index: 9999;
  }
}
.is-dark{
  .multiselect{
    ::v-deep(.multiselect-group-label){
      background-color: var(--dark-sidebar-light-2);
      color: var(--dark-dark-text);
    }
  }
}
</style>
